import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useArtists } from "../../context/ArtistContext";

/* ---------------------------
   Helpers (same logic as you)
---------------------------- */
function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function cleanTag(t) {
  return String(t || "").replace(/^#/, "").trim();
}
function getArtworkSnapshot(a) {
  if (!a) return null;

  const image_url =
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    "";

  return {
    id: a?.id ?? a?.artwork_id ?? a?.artworkId ?? null,
    title: a?.title || "Untitled",
    description: a?.description || a?.caption || a?.subtitle || "",
    image_url,
    tags: Array.isArray(a?.tags) ? a.tags : [],
    merchLinks: Array.isArray(a?.merchLinks) ? a.merchLinks : [],
    views: a?.views,
    likes: a?.likes,
  };
}

function getImageUrl(artwork) {
  return (
    artwork?.image_url ||
    artwork?.imageUrl ||
    artwork?.thumbnail_url ||
    artwork?.thumb_url ||
    artwork?.cover_url ||
    artwork?.media_url ||
    ""
  );
}

/* ---------------------------
   Shared modal open behavior
---------------------------- */
function useArtistModalNav({ artwork, hubState }) {
  const nav = useNavigate();
  const location = useLocation();
  const { upsertArtist, getArtist } = useArtists();

  useEffect(() => {
    if (artwork?.artist?.id) upsertArtist(artwork.artist);
  }, [
    artwork?.artist?.id,
    artwork?.artist?.display_name,
    artwork?.artist?.avatar_url,
    upsertArtist,
  ]);

  const liveArtist = useMemo(() => {
    const id = artwork?.artist?.id;
    return (id && getArtist(id)) || artwork?.artist || {};
  }, [artwork?.artist, artwork?.artist?.id, getArtist]);

  function openArtistModal(e) {
    e?.stopPropagation?.();
    e?.preventDefault?.();

    const artistId = liveArtist?.id ?? artwork?.artist?.id;
    if (!artistId) return;

    nav(`/arthouse/artist-modal/${artistId}`, {
      state: {
        backgroundLocation: location,
        ...(hubState || {}),
        artwork: getArtworkSnapshot(artwork),
      },
    });
  }

  return { liveArtist, openArtistModal };
}

/* =========================================================
   1) SpotlightPieceCard
   - Matches the small horizontal cards under the hero
   - Has: image, bottom bar, title, byline, heart count
========================================================= */
export function SpotlightPieceCard({ artwork, hubState }) {
  const { liveArtist, openArtistModal } = useArtistModalNav({ artwork, hubState });

  const title = artwork?.title || "Untitled";
  const creator = liveArtist?.display_name || "Unknown";
  const imageUrl = getImageUrl(artwork);

  const hasLikes = typeof artwork?.likes !== "undefined";
  const likes = safeNum(artwork?.likes);

  const likeLabel =
    likes >= 1000 ? `${(likes / 1000).toFixed(likes >= 10000 ? 0 : 1)}k` : String(likes);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openArtistModal}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openArtistModal(e);
        }
      }}
      aria-label={`Open artist: ${creator}`}
      className="
        group relative
        mx-auto shrink-0 snap-center
        h-[132px] w-[250px] sm:h-[142px] sm:w-[280px]
        overflow-hidden cursor-pointer select-none
        rounded-[18px]
        border border-white/10
        bg-white/[0.03] backdrop-blur-xl
        shadow-[0_18px_70px_rgba(0,0,0,0.55)]
        transition-all duration-300
        hover:bg-white/[0.045]
        hover:-translate-y-[1px]
        hover:shadow-[0_28px_110px_rgba(0,0,0,0.72)]
        focus:outline-none
        focus-visible:ring-2 focus-visible:ring-sky-400/30
        focus-visible:ring-offset-0
      "
    >
      {/* Background media */}
      <div className="absolute inset-0">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            loading="lazy"
            className="
              h-full w-full object-cover
              opacity-[0.98]
              transition-transform duration-700
              group-hover:scale-[1.045]
              will-change-transform
            "
          />
        ) : (
          <div className="h-full w-full bg-white/[0.06]" />
        )}

        {/* Contrast + cinematic depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
        <div className="absolute inset-0 bg-[radial-gradient(110%_70%_at_50%_0%,rgba(255,255,255,0.10),transparent_55%)]" />
      </div>

      {/* Likes (top-left) */}
      {hasLikes ? (
        <div className="absolute left-3 top-3 z-10">
          <div
            className="
              inline-flex items-center gap-2
              rounded-full
              border border-white/12
              bg-black/35 backdrop-blur-2xl
              px-2.5 py-1.5
              text-[11px] font-semibold text-white/85
              shadow-[0_10px_30px_rgba(0,0,0,0.35)]
            "
          >
            <span className="opacity-80">♡</span>
            {likeLabel}
          </div>
        </div>
      ) : null}

      {/* Bottom glass info */}
      <div className="absolute left-3 right-3 bottom-3 z-10">
        <div
          className="
            rounded-[16px]
            border border-white/12
            bg-black/40 backdrop-blur-2xl
            shadow-[0_16px_70px_rgba(0,0,0,0.40)]
            px-3 py-2.5
          "
        >
          <div className="text-[11px] font-medium tracking-wide text-white/65">
            Featured piece
          </div>

          <div className="mt-1 truncate text-[13.5px] font-semibold text-white/92">
            {title}
          </div>

          <div className="mt-1 truncate text-[11px] text-white/60">
            by <span className="text-white/70">{creator}</span>
          </div>
        </div>
      </div>

      {/* Hover ring/glow */}
      <div
        className="
          pointer-events-none absolute inset-0 rounded-[18px]
          ring-1 ring-white/0 transition
          group-hover:ring-white/12
        "
      />
      <div
        className="
          pointer-events-none absolute -inset-6 opacity-0 transition duration-300
          group-hover:opacity-100
          bg-[radial-gradient(60%_60%_at_50%_50%,rgba(56,189,248,0.12),transparent_60%)]
        "
      />
    </div>
  );
}


/* =========================================================
   2) ShowcaseArtworkCard
   - Matches the Community Showcase grid cards (bottom)
   - Landscape image, bottom overlay, title, heart+views
========================================================= */
export function ShowcaseArtworkCard({ artwork, hubState }) {
  const { liveArtist, openArtistModal } = useArtistModalNav({ artwork, hubState });

  const title = artwork?.title || "Untitled";
  const creator = liveArtist?.display_name || "Unknown";

  const imageUrl = getImageUrl(artwork);

  const hasLikes = typeof artwork?.likes !== "undefined";
  const hasViews = typeof artwork?.views !== "undefined";

  const likes = safeNum(artwork?.likes);
  const views = safeNum(artwork?.views);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openArtistModal}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openArtistModal(e);
        }
      }}
      aria-label={`Open artwork: ${title} by ${creator}`}
      className={[
        // MUST: let the card fill whatever grid cell size it gets
        "relative h-full w-full min-w-0",
        "cursor-pointer overflow-hidden",
        "rounded-[22px]",
        "border border-white/10",
        "bg-white/[0.02] backdrop-blur-xl",
        "shadow-[0_18px_90px_rgba(0,0,0,0.55)]",
        "hover:bg-white/[0.03] hover:shadow-[0_30px_140px_rgba(0,0,0,0.78)]",
        "transition",
        "focus:outline-none focus:ring-2 focus:ring-sky-400/25",
        "group",
      ].join(" ")}
    >
      {/* Grid-friendly media area:
          - Remove fixed aspect ratio so tall/wide tiles can breathe.
          - Make the media fill the whole card height.
      */}
      <div className="relative h-full w-full bg-black/25 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            loading="lazy"
            className={[
              "absolute inset-0 h-full w-full",
              // Fill the tile, crop as needed (your grid is editorial)
              "object-cover",
              "opacity-[0.99]",
              "group-hover:scale-[1.05]",
              "transition-transform duration-700",
            ].join(" ")}
          />
        ) : (
          <div className="absolute inset-0 bg-white/[0.06]" />
        )}

        {/* subtle vignette (always very light) */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10" />

        {/* sheen */}
        <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition">
          <div className="absolute -top-28 left-1/2 h-56 w-[620px] -translate-x-1/2 rotate-6 bg-white/10 blur-[70px]" />
        </div>

        {/* Hover-only overlay */}
        <div className="pointer-events-none absolute inset-0">
          {/* darken only on hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-gradient-to-t from-black/75 via-black/30 to-transparent" />

          {/* bottom info panel - hidden until hover */}
          <div
            className={[
              "absolute left-3 right-3 bottom-3",
              "translate-y-3 opacity-0",
              "group-hover:translate-y-0 group-hover:opacity-100",
              "transition duration-300",
            ].join(" ")}
          >
            <div
              className={[
                "rounded-[18px]",
                "border border-white/12",
                "bg-black/40 backdrop-blur-2xl",
                "shadow-[0_16px_70px_rgba(0,0,0,0.45)]",
                "px-3.5 py-3",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-semibold text-white/92">
                    {title}
                  </div>
                  <div className="mt-1 truncate text-[11px] text-white/60">
                    by {creator}
                  </div>
                </div>

                <div className="shrink-0 flex items-center gap-3 text-[11px] text-white/80">
                  {hasLikes ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="opacity-85">♡</span>
                      {likes >= 1000
                        ? `${(likes / 1000).toFixed(likes >= 10000 ? 0 : 1)}k`
                        : likes}
                    </span>
                  ) : null}
                  {hasViews ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="opacity-80">⟡</span>
                      {views >= 1000
                        ? `${(views / 1000).toFixed(views >= 10000 ? 0 : 1)}k`
                        : views}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* hover ring */}
        <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-white/0 group-hover:ring-white/10 transition" />

        {/* optional: tiny corner hint icon (kept invisible unless hover)
            Remove if you don’t want extra UI.
        */}
        <div className="pointer-events-none absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition">
          <div className="rounded-full border border-white/10 bg-black/35 backdrop-blur-xl px-2.5 py-1 text-[10px] text-white/70">
            View
          </div>
        </div>
      </div>
    </div>
  );
}
